// src/services/ExternalModelService.js
// Registry for "paper models" — external/proprietary risk models that are not
// implemented as RMS modules but still need to be documented as risk-mitigating
// controls in BWRA (e.g. third-party PEP screening, internal spreadsheet tools).
import auditService from './AuditService';
import moduleFreshnessService from './ModuleFreshnessService';
import storage from './StorageAdapter';
import entityService from './EntityService';
import ServiceValidator from './ServiceValidator';
import externalModelsApi from '../api/external-models';
import { makeDualWriteMixin, applyDualWriteMixin } from './_dualWriteHelpers';

const VALID_STATUSES = ['draft', 'submitted', 'approved'];
const VALID_TYPES = ['EXTERNAL_VENDOR', 'INTERNAL_PROPRIETARY', 'SPREADSHEET', 'OTHER'];

class ExternalModelService {
  constructor() {
    this.MODELS_KEY = 'externalmodels_models';
    this.listeners = [];
    if (typeof window !== 'undefined') {
      window.addEventListener('rms_active_entity_changed', () => {
        this.notifyListeners();
      });
    }
  }

  _entityKey(baseKey) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${baseKey}_${entityId}` : baseKey;
  }

  _getCurrentUser() {
    try {
      const session = storage.getJSON('rms_session', null);
      return session?.username || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  // === CRUD ===

  getModels() {
    return storage.getJSON(this._entityKey(this.MODELS_KEY), []);
  }

  getModel(id) {
    return this.getModels().find(m => m.id === id) || null;
  }

  generateModelId() {
    const models = this.getModels();
    const numericIds = models
      .map(m => parseInt(String(m.id || '').replace('EXT-', ''), 10))
      .filter(n => !isNaN(n));
    const max = numericIds.length > 0 ? Math.max(...numericIds) : 0;
    return `EXT-${String(max + 1).padStart(3, '0')}`;
  }

  async addModel(modelData) {
    ServiceValidator.validate(modelData, {
      name: { required: true, type: 'string', minLength: 1 },
    }, 'ExternalModelService.addModel');
    if (!Array.isArray(modelData.declaredRisks) || modelData.declaredRisks.length === 0) {
      throw new Error('ExternalModelService.addModel: declaredRisks must contain at least one risk ID');
    }
    const eff = parseInt(modelData.effectiveness, 10);
    if (isNaN(eff) || eff < 1 || eff > 5) {
      throw new Error('ExternalModelService.addModel: effectiveness must be an integer 1-5');
    }
    const modelType = modelData.modelType || 'EXTERNAL_VENDOR';
    if (!VALID_TYPES.includes(modelType)) {
      throw new Error(`ExternalModelService.addModel: invalid modelType "${modelType}"`);
    }
    const now = new Date().toISOString();
    const newModel = {
      id: this.generateModelId(),
      name: String(modelData.name).trim(),
      description: modelData.description || '',
      vendor: modelData.vendor || '',
      ownerInternal: modelData.ownerInternal || '',
      modelType,
      version: modelData.version || '',
      declaredRisks: [...modelData.declaredRisks],
      effectiveness: eff,
      approvalStatus: 'draft',
      approvedBy: null,
      approvedAt: null,
      approvalHistory: [],
      documentation: modelData.documentation || { links: [], notes: '', contractRef: '' },
      validityPeriod: modelData.validityPeriod || null,
      createdAt: now,
      updatedAt: now,
    };
    const models = this.getModels();
    models.push(newModel);
    await this._dualWriteJson(this.MODELS_KEY, models);
    await moduleFreshnessService.markModified('ExternalModels');
    auditService.log('CREATE', 'ExternalModels', { entity: 'ExternalModel', id: newModel.id, name: newModel.name });
    this.notifyListeners();
    return newModel;
  }

  async updateModel(id, updates) {
    ServiceValidator.requireString(id, 'id', 'ExternalModelService.updateModel');
    const models = this.getModels();
    const idx = models.findIndex(m => m.id === id);
    if (idx < 0) throw new Error(`ExternalModelService.updateModel: model "${id}" not found`);
    const existing = models[idx];

    // Approval-locked: cannot edit declaredRisks/effectiveness/name once approved
    // without going back to draft. The UI gates this; service enforces it.
    if (existing.approvalStatus === 'approved') {
      const protectedFields = ['name', 'declaredRisks', 'effectiveness', 'modelType'];
      for (const f of protectedFields) {
        if (Object.prototype.hasOwnProperty.call(updates, f) &&
            JSON.stringify(updates[f]) !== JSON.stringify(existing[f])) {
          throw new Error(`ExternalModelService.updateModel: cannot modify "${f}" while approved`);
        }
      }
    }

    if (updates.declaredRisks !== undefined) {
      if (!Array.isArray(updates.declaredRisks) || updates.declaredRisks.length === 0) {
        throw new Error('ExternalModelService.updateModel: declaredRisks must contain at least one risk ID');
      }
    }
    if (updates.effectiveness !== undefined) {
      const eff = parseInt(updates.effectiveness, 10);
      if (isNaN(eff) || eff < 1 || eff > 5) {
        throw new Error('ExternalModelService.updateModel: effectiveness must be 1-5');
      }
      updates.effectiveness = eff;
    }
    if (updates.modelType !== undefined && !VALID_TYPES.includes(updates.modelType)) {
      throw new Error(`ExternalModelService.updateModel: invalid modelType "${updates.modelType}"`);
    }

    const merged = {
      ...existing,
      ...updates,
      id: existing.id,
      approvalStatus: existing.approvalStatus,
      approvedBy: existing.approvedBy,
      approvedAt: existing.approvedAt,
      approvalHistory: existing.approvalHistory,
      createdAt: existing.createdAt,
      updatedAt: new Date().toISOString(),
    };
    models[idx] = merged;
    await this._dualWriteJson(this.MODELS_KEY, models);
    await moduleFreshnessService.markModified('ExternalModels');
    auditService.log('UPDATE', 'ExternalModels', { entity: 'ExternalModel', id, name: merged.name });
    this.notifyListeners();
    return merged;
  }

  async deleteModel(id) {
    const models = this.getModels();
    const target = models.find(m => m.id === id);
    if (!target) return false;
    const filtered = models.filter(m => m.id !== id);
    await this._dualWriteJson(this.MODELS_KEY, filtered);
    await moduleFreshnessService.markModified('ExternalModels');
    auditService.log('DELETE', 'ExternalModels', { entity: 'ExternalModel', id, name: target.name });
    this.notifyListeners();
    return true;
  }

  // === GOVERNANCE WORKFLOW ===

  async submitModelForReview(id) {
    return this._transition(id, 'submitted', '');
  }

  async approveModel(id, comment = '') {
    return this._transition(id, 'approved', comment);
  }

  async rejectModel(id, comment = '') {
    if (!comment || !String(comment).trim()) {
      throw new Error('ExternalModelService.rejectModel: comment is required');
    }
    return this._transition(id, 'draft', comment, /* viaReject */ true);
  }

  async _transition(id, toStatus, comment, viaReject = false) {
    if (!VALID_STATUSES.includes(toStatus)) {
      throw new Error(`ExternalModelService: invalid target status "${toStatus}"`);
    }
    const models = this.getModels();
    const idx = models.findIndex(m => m.id === id);
    if (idx < 0) throw new Error(`ExternalModelService: model "${id}" not found`);
    const existing = models[idx];
    const fromStatus = existing.approvalStatus || 'draft';

    // Allowed transitions:
    //   draft → submitted (submit)
    //   submitted → approved (approve)
    //   submitted → draft (reject)
    //   approved → draft (re-open via reject)
    const allowed =
      (fromStatus === 'draft' && toStatus === 'submitted') ||
      (fromStatus === 'submitted' && toStatus === 'approved') ||
      (fromStatus === 'submitted' && toStatus === 'draft' && viaReject) ||
      (fromStatus === 'approved' && toStatus === 'draft' && viaReject);
    if (!allowed) {
      throw new Error(`ExternalModelService: cannot transition from "${fromStatus}" to "${toStatus}"`);
    }

    const now = new Date().toISOString();
    const by = this._getCurrentUser();
    const historyEntry = { from: fromStatus, to: toStatus, by, at: now, comment: comment || '' };
    const updated = {
      ...existing,
      approvalStatus: toStatus,
      approvedBy: toStatus === 'approved' ? by : null,
      approvedAt: toStatus === 'approved' ? now : null,
      approvalHistory: [...(existing.approvalHistory || []), historyEntry],
      updatedAt: now,
    };
    models[idx] = updated;
    await this._dualWriteJson(this.MODELS_KEY, models);
    await moduleFreshnessService.markModified('ExternalModels');
    auditService.log('STATUS_TRANSITION', 'ExternalModels', {
      entity: 'ExternalModel',
      id, name: updated.name,
      from: fromStatus, to: toStatus, by,
    });
    this.notifyListeners();
    return updated;
  }

  // === BWRA RISK COVERAGE (kontraktsmetod) ===

  // Returns { riskId: [{ id, name, status, vendor, modelType, effectiveness, description }] }
  // Only approved models are surfaced as controls in BWRA. Drafts and submitted
  // models are still visible in the External Models UI but do not influence
  // residual risk calculations.
  getDeclaredRiskCoverage() {
    const models = this.getModels().filter(m => m.approvalStatus === 'approved');
    const coverage = {};
    models.forEach(m => {
      (m.declaredRisks || []).forEach(riskId => {
        if (!coverage[riskId]) coverage[riskId] = [];
        coverage[riskId].push({
          id: m.id,
          name: m.name,
          status: m.approvalStatus,
          vendor: m.vendor,
          modelType: m.modelType,
          effectiveness: m.effectiveness,
          description: m.description || '',
        });
      });
    });
    return coverage;
  }

  // === CHANGE LISTENERS ===

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(l => {
      try { l(); } catch { /* ignore listener errors */ }
    });
  }

  // INFRA-181: backend dual-write + initFromBackend.
  async initFromBackend() {
    return this._initFromBackend([this.MODELS_KEY], []);
  }
}

applyDualWriteMixin(ExternalModelService, makeDualWriteMixin({
  storage, api: externalModelsApi, serviceName: 'ExternalModelService',
}));

const externalModelService = new ExternalModelService();

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.externalModelService = externalModelService;
}

export default externalModelService;
